import { useState } from 'react'

import { useAction } from '../components/toast'
import { Badge, Card, Loaded, Modal, Notice, Page, Table } from '../components/ui'
import { del, get, put } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { dateTime } from '../lib/format'
import { useResource } from '../lib/useResource'

type Credential = {
  provider: string
  label: string
  secret_label: string
  configured: boolean
  /** "console" | "environment" | "unset" — where the live value comes from. */
  source: string
  public_value: string
  public_label: string
  environment_fallback: boolean
  updated_by: string | null
  updated_at: string | null
}

type Gateway = { gateway: string; currencies: string[]; ready: boolean }

/** Reasons are enforced by the API at ten characters; say so before the round trip. */
const MIN_REASON = 10

function sourceBadge(row: Credential) {
  if (row.source === 'console') return <Badge tone="green">set here</Badge>
  if (row.source === 'environment') return <Badge tone="cyan">from .env</Badge>
  return <Badge tone="orange">not set</Badge>
}

export function PaymentGateways() {
  const { can } = useAuth()
  const run = useAction()
  const editable = can(P.billingWrite)

  const credentials = useResource((signal) =>
    get<Credential[]>('/v1/admin/payments/credentials', undefined, signal),
  )
  const gateways = useResource((signal) =>
    get<Gateway[]>('/v1/admin/payments/gateways', undefined, signal),
  )

  const [editing, setEditing] = useState<Credential | null>(null)
  const [clearing, setClearing] = useState<Credential | null>(null)

  const reload = () => {
    credentials.reload()
    gateways.reload()
  }

  return (
    <Page
      title="Payment gateways"
      subtitle="The keys that take the money, and whether each gateway can actually charge."
    >
      <div className="stack">
        <Loaded resource={gateways}>
          {(rows) => (
            <Card
              title="Can we take a payment?"
              hint="Which gateway settles which currency, and whether it is set up."
            >
              <Table
                columns={[
                  {
                    key: 'gateway',
                    header: 'Gateway',
                    strong: true,
                    render: (row) => row.gateway,
                  },
                  {
                    key: 'currencies',
                    header: 'Settles',
                    render: (row) => row.currencies.join(', '),
                  },
                  {
                    key: 'ready',
                    header: '',
                    render: (row) =>
                      row.ready ? (
                        <Badge tone="green">ready</Badge>
                      ) : (
                        <Badge tone="alert">cannot charge</Badge>
                      ),
                  },
                ]}
                rows={rows}
                rowKey={(row) => row.gateway}
              />
              {rows.some((row) => !row.ready) && (
                <div style={{ padding: '0 18px 16px' }}>
                  <Notice tone="warn">
                    A gateway with no key is a shop with the lights on and the till
                    missing — a customer finds out before you do. Set its secret below.
                  </Notice>
                </div>
              )}
            </Card>
          )}
        </Loaded>

        <Card
          title="Keys"
          hint="Set here, a key takes effect immediately — no redeploy. Secrets are never shown again once saved."
        >
          <Loaded resource={credentials} emptyWhen={(rows) => rows.length === 0}>
            {(rows) => (
              <Table
                columns={[
                  { key: 'label', header: 'What', strong: true, render: (row) => row.label },
                  {
                    key: 'public',
                    header: 'Public identifier',
                    render: (row) =>
                      row.public_label ? (
                        <span className="mono">
                          {row.public_value || <span className="dim">—</span>}
                        </span>
                      ) : (
                        <span className="dim">n/a</span>
                      ),
                  },
                  { key: 'source', header: 'Source', render: sourceBadge },
                  {
                    key: 'updated',
                    header: 'Last changed',
                    render: (row) =>
                      row.updated_at ? (
                        <span title={row.updated_at}>{dateTime(row.updated_at)}</span>
                      ) : (
                        <span className="dim">—</span>
                      ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    render: (row) =>
                      editable ? (
                        <div className="row">
                          <button className="btn btn--sm" onClick={() => setEditing(row)}>
                            {row.source === 'console' ? 'Replace' : 'Set'}
                          </button>
                          {row.source === 'console' && (
                            <button
                              className="btn btn--sm btn--ghost"
                              onClick={() => setClearing(row)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ) : null,
                  },
                ]}
                rows={rows}
                rowKey={(row) => row.provider}
              />
            )}
          </Loaded>
        </Card>

        {!editable && (
          <Notice>
            Changing a gateway key needs <span className="mono">billing:write</span>.
            Reading accounts and seeing billing is deliberately not enough — only this
            permission can redirect where the money settles.
          </Notice>
        )}
      </div>

      {editing && (
        <EditKey
          credential={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}

      {clearing && (
        <RemoveKey
          credential={clearing}
          onClose={() => setClearing(null)}
          onConfirm={async (reason) => {
            await run(
              () =>
                del(
                  `/v1/admin/payments/credentials/${clearing.provider}`,
                  { reason },
                ),
              clearing.environment_fallback
                ? 'Removed. The value from the environment is live again.'
                : 'Removed. This gateway can no longer charge.',
              () => {
                setClearing(null)
                reload()
              },
            )
          }}
        />
      )}
    </Page>
  )
}

function EditKey({
  credential,
  onClose,
  onSaved,
}: {
  credential: Credential
  onClose: () => void
  onSaved: () => void
}) {
  const run = useAction()
  const [secret, setSecret] = useState('')
  const [publicValue, setPublicValue] = useState(credential.public_value)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const reasonShort = reason.trim().length > 0 && reason.trim().length < MIN_REASON
  const canSave =
    secret.trim().length > 0 && reason.trim().length >= MIN_REASON && !busy

  const save = async () => {
    setBusy(true)
    await run(
      () =>
        put(`/v1/admin/payments/credentials/${credential.provider}`, {
          api_key: secret.trim(),
          public_value: publicValue.trim(),
          reason: reason.trim(),
        }),
      'Saved. It is live now — no redeploy needed.',
      onSaved,
    )
    setBusy(false)
  }

  return (
    <Modal title={credential.label} onClose={onClose}>
      <div className="stack">
        <Notice tone="warn">
          Check you are pasting the right mode. A test secret in production takes no
          money and a live secret in a test deployment takes real money — nothing here
          can tell the difference for you.
        </Notice>

        {credential.public_label && (
          <div className="field">
            <label className="field__label" htmlFor="pay-public">
              {credential.public_label}
            </label>
            <input
              id="pay-public"
              className="input mono"
              value={publicValue}
              onChange={(event) => setPublicValue(event.target.value)}
            />
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Public by design — this one ships inside the app.
            </p>
          </div>
        )}

        <div className="field">
          <label className="field__label" htmlFor="pay-secret">
            {credential.secret_label}
          </label>
          <input
            id="pay-secret"
            className="input mono"
            type="password"
            autoComplete="off"
            value={secret}
            autoFocus
            placeholder={
              credential.configured ? 'Replaces the current secret' : 'Not set yet'
            }
            onChange={(event) => setSecret(event.target.value)}
          />
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Stored encrypted and never shown again — not even to you. If you lose it,
            generate a new one in the gateway's dashboard.
          </p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="pay-reason">
            Why are you changing it?
          </label>
          <input
            id="pay-reason"
            className="input"
            value={reason}
            placeholder="Rotating after the old key was exposed"
            onChange={(event) => setReason(event.target.value)}
          />
          {reasonShort && (
            <p className="muted" style={{ margin: '4px 0 0' }}>
              A few more words — this is what a review of the change will read.
            </p>
          )}
        </div>

        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={!canSave}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save key'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function RemoveKey({
  credential,
  onClose,
  onConfirm,
}: {
  credential: Credential
  onClose: () => void
  onConfirm: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <Modal title={`Remove the ${credential.label} key`} onClose={onClose}>
      <div className="stack">
        {/* What actually happens differs, and guessing wrong here means either
            an unexplained outage or a false sense of having switched it off. */}
        {credential.environment_fallback ? (
          <Notice>
            This removes the key set here. The value from the deployment's environment
            takes over immediately, so this gateway keeps charging — that is what makes
            this a safe way to undo a bad edit.
          </Notice>
        ) : (
          <Notice tone="warn">
            There is no environment value behind this one. Removing it means this
            gateway stops being able to take payments straight away.
          </Notice>
        )}

        <div className="field">
          <label className="field__label" htmlFor="pay-remove-reason">
            Why are you removing it?
          </label>
          <input
            id="pay-remove-reason"
            className="input"
            value={reason}
            autoFocus
            placeholder="Rolling back a key pasted in error"
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn--danger"
            disabled={busy || reason.trim().length < MIN_REASON}
            onClick={() => {
              setBusy(true)
              void onConfirm(reason.trim()).finally(() => setBusy(false))
            }}
          >
            {busy ? 'Removing…' : 'Remove key'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
